// Direct module import: the @acme/util barrel would pull AI SDKs and the tokenizer.
import { AI_PROVIDERS, allModelOptions, type AIProvider } from "@acme/util/src/constant/model";

interface LandingModelListProps {
    /** Providers to list; models appear newest first within each. */
    providers: AIProvider[];
    /** Max models per provider. */
    perProvider?: number;
    /** Show the provider name above each group (multi-provider lists). */
    showProvider?: boolean;
}

/**
 * Model names straight from MODEL_REGISTRY, so landing pages stay current when
 * models are added or retired instead of advertising "Claude 3" for a year.
 * Rendered as real text (not images) so the names are indexable.
 */
export const LandingModelList = ({ providers, perProvider = 4, showProvider = false }: LandingModelListProps) => {
    const groups = providers.map(provider => ({
        provider,
        models: allModelOptions.filter(m => m.type === provider).slice(0, perProvider),
    })).filter(g => g.models.length > 0);

    return (
        <div className="w-full flex flex-wrap justify-center gap-x-12 gap-y-8">
            {groups.map(group => (
                <div key={group.provider} className="flex flex-col items-center gap-3">
                    {showProvider && (
                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">{AI_PROVIDERS[group.provider].name}</p>
                    )}
                    <ul className="flex flex-wrap justify-center gap-2 list-none p-0 m-0">
                        {group.models.map(model => (
                            <li
                                key={model.value}
                                title={model.desc}
                                className="list-none rounded-full border border-gray-200 dark:border-gray-700 px-3 py-1 text-sm font-medium text-gray-800 dark:text-gray-200"
                            >
                                {model.label}
                                {model.isNew && <span className="ml-1.5 text-[10px] font-semibold uppercase text-blue-600 dark:text-blue-400">New</span>}
                            </li>
                        ))}
                    </ul>
                </div>
            ))}
        </div>
    );
};
